/**
 * useShellReactions - every reaction that is owed, and this client's answers to them.
 *
 * EVERY REACTION THAT IS OWED, not only the one on the tab I happen to be
 * looking at - `useReactionQueue` owns that; volume is decided locally from
 * my own actor pointer, via `isControlledByActor` (a network confers no
 * authority; it only decides whose alarm rings).
 *
 * - ANSWER SOMEBODY ELSE'S REACTION: pick the character up, then point the
 *   prompt at them. Focusing alone would show a Dodge button for somebody
 *   you are not playing. Nothing is synced or claimed - the resolution
 *   transaction still decides who answers.
 * - NOBODY ELSE IS REACTING: apply the rest, SEQUENTIALLY - each resolution
 *   applies hits and pushes history, and five at once would interleave.
 * - The local `pendingAttack` follows the shared defence: resolved by
 *   anyone (routing a Furious Assault prompt to the ATTACKER's client), or
 *   cleared - `seenFsDefenceRef` tells "not yet synced" from "was deleted".
 * - GM FORCE-CLEAR clears the prompt ON SCREEN, not the tab being looked at.
 *
 * Extracted from DeathWatchSimulator.
 */
import { useEffect, useRef, useCallback } from 'react';
import { DEFENCE_TIMEOUT_MS } from '../engine/constants';
import { isControlledBy } from '../engine/roster/control';
import useReactionQueue from './useReactionQueue';

/**
 * What the local pending attack should do about the shared defence doc:
 * 'wait', 'clear', or 'resolved' (clear, maybe after a Furious Assault).
 */
export function defenceVerdict(fsDefence, seen) {
  if (fsDefence) return fsDefence.resolvedBy ? 'resolved' : 'wait';
  return seen ? 'clear' : 'idle';
}

/** Furious Assault goes to the attacker's own client, once. */
export function furiousAssaultFor(fsDefence, pendingAttack, playerId, pendingBonusAttack) {
  if (!fsDefence.furAssaultEligible || fsDefence.attackerId !== playerId || pendingBonusAttack) return null;
  return {
    talentName: 'Furious Assault',
    slot: pendingAttack.slot,
    tabId: pendingAttack.tabId,
    modifiedSkill: pendingAttack.modifiedSkill,
    actorId: pendingAttack.actorId ?? null,
  };
}

/** One pass of the pending attack against the shared defence doc. */
function followDefence(d, seenRef) {
  if (!d.sessionId || !d.pendingAttack) {
    seenRef.current = false;
    return;
  }
  const fsDefence = d.getDefenceForTab(d.pendingAttack.tabId);
  const verdict = defenceVerdict(fsDefence, seenRef.current);
  if (verdict === 'wait') seenRef.current = true;
  if (verdict === 'resolved') {
    const bonus = furiousAssaultFor(fsDefence, d.pendingAttack, d.playerId, d.pendingBonusAttack);
    if (bonus) d.setPendingBonusAttack(bonus);
  }
  if (verdict === 'resolved' || verdict === 'clear') {
    d.setPendingAttack(null);
    seenRef.current = false;
  }
}

/** Clear the local pending attack when the shared defence is resolved or gone. */
function usePendingAttackFollowsDefence(d) {
  const seenRef = useRef(false);
  const { sessionId, pendingAttack, getDefenceForTab, setPendingAttack, playerId } = d;
  const { pendingBonusAttack, setPendingBonusAttack } = d;
  useEffect(() => {
    followDefence(
      {
        sessionId,
        pendingAttack,
        getDefenceForTab,
        setPendingAttack,
        playerId,
        pendingBonusAttack,
        setPendingBonusAttack,
      },
      seenRef,
    );
  }, [
    sessionId,
    pendingAttack,
    getDefenceForTab,
    setPendingAttack,
    playerId,
    pendingBonusAttack,
    setPendingBonusAttack,
  ]);
}

/** The queue, with my actor's reach deciding the volume. */
function useQueue(d) {
  const { roster, actorId } = d;
  const isControlledByActor = useCallback(
    (combatantId) => isControlledBy(roster, combatantId, actorId),
    [roster, actorId],
  );
  return useReactionQueue({
    pendingDefences: d.pendingDefences,
    pendingAttack: d.pendingAttack,
    currentTab: d.currentTab,
    roster,
    actorId,
    isGm: d.isGm,
    sessionId: d.sessionId,
    alarmPolicy: d.reactionAlarmPolicy,
    isControlledByActor,
    playerId: d.playerId,
    chainTabId: d.defenceChain?.tabId ?? null,
    timeoutMs: DEFENCE_TIMEOUT_MS,
  });
}

/** The GM's force-clear: the shared doc for the prompt on screen, and the local attack. */
function useForceClear({ sessionId, clearPendingDefenceFirestore, activeTab, setPendingAttack }, focusedReactionId) {
  return useCallback(() => {
    const clearing = focusedReactionId || activeTab;
    if (sessionId && clearPendingDefenceFirestore) clearPendingDefenceFirestore(clearing);
    setPendingAttack(null);
  }, [sessionId, clearPendingDefenceFirestore, focusedReactionId, activeTab, setPendingAttack]);
}

/**
 * @param {object} args - pendingDefences, pendingAttack, setPendingAttack,
 *   currentTab, roster, actorId, setActorId, isGm, sessionId, playerId,
 *   activeTab, reactionAlarmPolicy, defenceChain, handleDefenceChoice,
 *   getDefenceForTab, clearPendingDefenceFirestore, pendingBonusAttack,
 *   setPendingBonusAttack.
 */
export default function useShellReactions(args) {
  const { setActorId, handleDefenceChoice } = args;
  const reactions = useQueue(args);
  const focusedReactionId = reactions.focusedId;
  const handleRespondToReaction = useCallback(
    (combatantId) => {
      setActorId(combatantId);
      reactions.focus(combatantId);
    },
    [setActorId, reactions],
  );
  const handleAcceptAllReactions = useCallback(async () => {
    for (const r of reactions.queued) {
      await handleDefenceChoice('none', { ...r.doc, tabId: r.combatantId });
    }
  }, [reactions.queued, handleDefenceChoice]);
  usePendingAttackFollowsDefence(args);
  const handleGmForceClear = useForceClear(args, focusedReactionId);
  return {
    reactions,
    effectivePendingDefence: reactions.focused?.doc ?? null,
    focusedReactionId,
    handleRespondToReaction,
    handleAcceptAllReactions,
    handleGmForceClear,
  };
}
